import { unsafeCSS, html } from 'lit';
import { customElement } from 'lit/decorators.js';

import { PopBaseComponent } from '../../base/pop-base-component.js';
import { Constants } from './calendar-year-chart.constants.js';
import type { CalendarYearChartContentObject } from './calendar-year-chart.model.js';
import { Options } from 'highcharts';
import { BeaconChartBase } from '@trp-beacon-ui-kit/foundation/components';
import { restrictLastLegendItemClick } from '../../common/utils/sharedChartOptions.js';
import { AssetClassEnum } from '../../common/enums/asset-class.js';
import { formatToNDecimalPlaces } from '../../common/utils/formatNumber';
import {
  DISCLAIMER_CODES,
  PerformanceTypes,
} from '../total-returns-chart/total-returns-chart.constants.js';
import style from './calendar-year-chart.scss?inline';

/**
 * @tag pop-calendar-year-chart-component
 * @attribute { CalendarYearChartContentObject } [data-component-object] CalendarYearChart content object
 */
@customElement(Constants.TAG_NAME)
export class PopCalendarYearChartComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * CalendarYearChart content object
   */
  componentObject?: CalendarYearChartContentObject;

  firstUpdated(): void {
    const chart = this.shadowRoot?.querySelector('beacon-bar-chart');

    if (chart) {
      // This is necessary because the pointFormatter option for the tooltip is a function and doesn't
      // work correctly when only passed in the options attribute to the beacon-bar-chart element.
      chart.options = this.getOptions();
    }
  }

  private getCategories() {
    return (
      this.componentObject?.itemList?.map((item) =>
        item.year ? item.year : '',
      ) || []
    );
  }

  private getSeriesData() {
    const assetClass = this.componentObject?.assetClass;

    const indicatorSymbol =
      assetClass === AssetClassEnum.FIXED_INCOME
        ? this.footnoteService?.getIndicatorSymbol(
            PerformanceTypes.TOTAL_RETURNS_FIXED_INCOME_DISCLAIMER_CODE,
          )
        : this.footnoteService?.getIndicatorSymbol(
            PerformanceTypes.TOTAL_RETURNS_DISCLAIMER_CODE,
          );

    const pureGrossSeries =
      this.componentObject?.itemList?.map((item) => ({
        y: item.pureGross,
        name: item.year,
      })) || [];

    const netSeries =
      this.componentObject?.itemList?.map((item) => ({
        y: item.net,
        name: item.year,
      })) || [];

    const benchmarkSeries =
      this.componentObject?.itemList?.map((item) => ({
        y: item.benchmark,
        name: item.year,
      })) || [];

    return [
      {
        name:
          assetClass === AssetClassEnum.FIXED_INCOME
            ? PerformanceTypes.PureGrossFixedIncome + indicatorSymbol
            : PerformanceTypes.PureGross + indicatorSymbol,
        data: pureGrossSeries,
      },
      {
        name:
          assetClass === AssetClassEnum.FIXED_INCOME
            ? PerformanceTypes.NetFixedIncome + indicatorSymbol
            : PerformanceTypes.Net + indicatorSymbol,
        data: netSeries,
      },
      {
        name: Constants.BENCHMARK_SERIES_NAME,
        data: benchmarkSeries,
      },
    ];
  }

  private getOptions(): Options {
    return {
      tooltip: {
        shared: true,
        useHTML: true,
        headerFormat: BeaconChartBase.tooltipHeaderHTML('{point.key}'),
        pointFormatter() {
          return BeaconChartBase.tooltipPointHTML(
            this,
            this.series.name,
            `${formatToNDecimalPlaces(this.y, 2)}%`,
          );
        },
      },
      exporting: { enabled: false },
      legend: restrictLastLegendItemClick(),
    };
  }

  /**
   * Temp fix for US637567
   * Highchart strips out disclaimer code attributes due to XSS prevention
   * Which prevent us from rendering the disclaimer code indication
   * Raised a feature request in the Foundation to allow these attributes
   * Remove this span once Foundation team allow customization of allowedAttributes in Highchart
   */
  private __renderHiddenDisclaimerCodes() {
    // we only have Net and Pure Gross in classifications
    const assetClass = this.componentObject?.assetClass;
    const disclaimerCode =
      assetClass === AssetClassEnum.FIXED_INCOME
        ? DISCLAIMER_CODES['TOTAL_RETURNS_FIXED_INCOME_DISCLAIMER_CODE']
        : DISCLAIMER_CODES['TOTAL_RETURNS_DISCLAIMER_CODE'];

    return html`<span
      class="calendar-year-chart__disclaimer"
      data-disclaimer-code="${disclaimerCode}"
    ></span>`;
  }

  render() {
    return this.renderComponent(
      html`<beacon-bar-chart
          variant="vertical"
          tooltip-variant="multi-level"
          categories="${this.getCategories()}"
          series="${JSON.stringify(this.getSeriesData())}"
          options="${JSON.stringify(this.getOptions())}"
          y-axis-label="${Constants.Y_AXIS_LABEL}"
          x-axis-label="${Constants.X_AXIS_LABEL}"
        >
        </beacon-bar-chart>
        ${this.__renderHiddenDisclaimerCodes()}`,
    );
  }
}
